/**
 * A money field: a `$` glued to the left of a box that shows `1,450.00`.
 *
 * The port of `Infrastructure/MoneyBox.cs`, and it keeps that file's one load-bearing decision:
 * THE FORMATTING IS NOT A CONVERTER ON THE VALUE. Every editor in this app reads its boxes on each
 * keystroke, because a live subtotal and a schedule preview depend on it - and a converter that
 * re-rendered `12` as `12.00` mid-keystroke would move the caret out from under the typist. So the
 * box reformats only when nobody is typing in it: on losing focus, and when something other than the
 * user sets the text (an editor opening, a catalog pick filling in a price).
 *
 * The value is read back as a plain string and parsed by the caller. `parseAmount` accepts the
 * grouping comma this writes, which is what closes the loop.
 */
import { insets, TextBox, type StackPanel, type TextBoxOptions } from 'webwin';
import { amount, parseAmount } from '../format.js';
import { CrmLabel } from './Text.js';
import {
  FONT,
  GAP,
  hstack,
  splitRow,
  textBox
} from './layout.js';

export interface MoneyBox {
  /** The row to put in a field: the `$` and the box together. */
  readonly row: StackPanel;
  readonly box: TextBox;
  /** The number in the box, or null when it is empty or not a number. */
  value(): number | null;
  /** Sets the box from a number, formatted. Null or undefined empties it. */
  set(value: number | null | undefined): void;
  /** The raw text, for a caller that wants to refuse a blank itself. */
  text(): string;
}

export function moneyBox(options: TextBoxOptions & { width?: number } = {}): MoneyBox {
  const box = textBox({ hAlign: 'stretch', ...options });

  const reformat = (): void => {
    if (box.focused) return;
    const parsed = parseAmount(box.text);
    if (parsed === null) return;
    const formatted = amount(parsed);
    if (box.text !== formatted) box.text = formatted;
  };

  // Both hooks are needed and neither is redundant. `blur` covers the typist leaving the field;
  // `textchanged`-while-unfocused covers everything else that writes to it, which is the case a
  // converter would have handled and a blur handler alone silently does not.
  box.on('blur', reformat);
  box.on('textchanged', reformat);

  const row = splitRow({ spacing: 2 });
  row.controls.add(
    new CrmLabel({
      text: '$',
      font: FONT.body,
      wrap: false,
      autoWidth: true,
      autoHeight: true,
      margin: insets(0, 0, GAP.xs, 0)
    })
  );
  row.controls.add(box);
  row.setPlacement(box, { fill: 1 });

  return {
    row,
    box,
    value: () => parseAmount(box.text),
    set: value => {
      box.text = value === null || value === undefined ? '' : amount(value);
    },
    text: () => box.text
  };
}
